<template>
  <div class="annotationTool">
    <template v-if="supportLine">
      <div
        v-for="[widthKey, item] in Object.entries(lineWidthMap)"
        :key="widthKey"
        :class="[
          'annotationTool__style',
          'annotationTool__lineWidth',
          {
            'annotationTool__style--active': item.value === config.strokeWidth,
          },
        ]"
        @click="handleLineWidthChange(item.value)"
      >
        <div class="line" :class="item.class" />
      </div>
      <div class="annotationTool__hr" />
      <div class="annotationTool__color-block" @click="handleSwitchColorPicker($event, 'stroke')">
        <div class="annotationTool__color" :style="{ background: config.stroke }" />
        <font-awesome-icon class="ml-2" :icon="['fas', 'chevron-down']" size="xs" />
      </div>
    </template>
    <template v-else-if="supportText">
      <div
        v-for="item in Object.values(textStyleMap)"
        :key="item.key"
        :class="[
          'annotationTool__style',
          {
            'annotationTool__style--active': item.value === config[item.key],
          },
        ]"
        @click="handleTextStyle(item.key, item.value)"
      >
        <font-awesome-icon :icon="item.icon" />
      </div>
      <div class="annotationTool__hr" />
      <div class="annotationTool__style" @click="handleSwitchColorPicker($event, 'fill')">
        <font-awesome-icon :icon="['fas', 'a']" size="xs" />
        <div class="annotationTool__text-color-style" :style="{ background: config.fill }" />
      </div>
      <div class="annotationTool__style" @click="handleSwitchColorPicker($event, 'backgroundColor')">
        <font-awesome-icon :icon="['fas', 'highlighter']" size="xs" />
        <div
          class="annotationTool__text-color-style"
          :class="{
            'annotationTool__color--transparent': config.backgroundColor === '',
          }"
          :style="{ background: config.backgroundColor }"
        />
      </div>
    </template>
    <BuilderAnnotationShapeTool
      v-else-if="supportShape"
      :config="config"
      @handleNoteStyleChange="$emit('handleNoteStyleChange', $event)"
    />
    <div class="annotationTool__colors-picker" v-show="isOpenColorPicker" :style="colorPickerStyle">
      <div
        v-if="colorKey === 'backgroundColor'"
        class="annotationTool__color annotationTool__color--transparent"
        @click="handleColorChange('')"
      />
      <div
        v-for="color in colors"
        :key="color"
        class="annotationTool__color"
        :style="{ background: color }"
        @click="handleColorChange(color)"
      />
    </div>
  </div>
</template>

<script>
import {
  ANNOTATION_COLORS,
  ANNOTATION_TYPE,
  ANNOTATION_LINE_WIDTH,
  ANNOTATION_LINE_WIDTH_VALUE,
  ANNOTATION_TEXT_STYLE,
} from "@/constants/annotationStatus";
import BuilderAnnotationShapeTool from "./BuilderAnnotationShapeTool";

export default {
  name: "BuilderAnnotationLineTool",
  props: {
    config: Object,
  },
  components: {
    BuilderAnnotationShapeTool,
  },
  data() {
    return {
      isOpenColorPicker: false,
      colors: ANNOTATION_COLORS,
      colorKey: "",
      colorPickerStyle: {
        left: 0,
        width: 0,
      },
      textStyleMap: {
        [ANNOTATION_TEXT_STYLE.BOLD]: {
          icon: ["fas", "bold"],
          key: "fontWeight",
          value: ANNOTATION_TEXT_STYLE.BOLD,
        },
        [ANNOTATION_TEXT_STYLE.ITALIC]: {
          icon: ["fas", "italic"],
          key: "fontStyle",
          value: ANNOTATION_TEXT_STYLE.ITALIC,
        },
      },
      lineWidthMap: {
        [ANNOTATION_LINE_WIDTH.THIN]: {
          class: "line--sm",
          value: ANNOTATION_LINE_WIDTH_VALUE.THIN,
        },
        [ANNOTATION_LINE_WIDTH.NORMAL]: {
          class: "",
          value: ANNOTATION_LINE_WIDTH_VALUE.NORMAL,
        },
        [ANNOTATION_LINE_WIDTH.BOLD]: {
          class: "line--lg",
          value: ANNOTATION_LINE_WIDTH_VALUE.BOLD,
        },
      },
    };
  },
  computed: {
    supportLine() {
      return [ANNOTATION_TYPE.ARROW, ANNOTATION_TYPE.PEN].includes(this.config.type);
    },
    supportText() {
      return ANNOTATION_TYPE.TEXT === this.config.type;
    },
    supportShape() {
      return ANNOTATION_TYPE.SHAPE === this.config.type;
    },
  },
  methods: {
    handleColorChange(color) {
      this.isOpenColorPicker = false;
      this.$emit("handleNoteStyleChange", {
        id: this.config.id,
        props: {
          [this.colorKey]: color,
        },
      });
    },
    handleLineWidthChange(value) {
      this.$emit("handleNoteStyleChange", {
        id: this.config.id,
        props: {
          strokeWidth: value,
        },
      });
    },
    handleSwitchColorPicker(e, colorKey) {
      if (this.isOpenColorPicker && this.colorKey === colorKey) {
        this.isOpenColorPicker = false;
        return;
      }
      const space = colorKey === "stroke" ? 9 : 0;
      this.colorKey = colorKey;
      this.colorPickerStyle.left = e.currentTarget.offsetLeft - space + "px";
      this.colorPickerStyle.width = e.currentTarget.clientWidth + "px";
      this.isOpenColorPicker = true;
    },
    handleTextStyle(key, value) {
      this.$emit("handleNoteStyleChange", {
        id: this.config.id,
        props: {
          [key]: this.config[key] === value ? "normal" : value,
        },
      });
    },
  },
  watch: {
    "config.id": {
      handler(selectedId) {
        if (selectedId) {
          this.isOpenColorPicker = false;
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
.annotationTool {
  --line-tool-height: 48px;
  --line-tool-bg: #2c2d32;
  position: relative;
  display: flex;
  justify-content: left;
  align-items: center;
  flex-direction: row;
  background: var(--line-tool-bg);
  min-height: var(--line-tool-height);
  padding-left: 8px;
  padding-right: 8px;
  width: auto;
  border-radius: 4px;
}

.annotationTool__style {
  position: relative;
  height: 32px;
  width: 32px;
  margin-right: 4px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-direction: column;
  gap: 4px;
}

.annotationTool__style:hover,
.annotationTool__style--active {
  background-color: rgba(255, 255, 255, 0.1);
}

.annotationTool__lineWidth {
  display: flex;
  align-items: center;
  justify-content: center;
  .line {
    background: white;
    width: 6px;
    height: 24px;
    border-radius: 20px;
    transform: rotate(45deg);
    &--sm {
      width: 4px;
    }
    &--lg {
      width: 8px;
    }
  }
}

.annotationTool__hr {
  width: 1px;
  height: 33px;
  background: #4a4a4a;
  margin: 0 4px;
}

.annotationTool__text-color-style {
  width: 24px;
  height: 4px;
}

.annotationTool__color-block {
  display: flex;
  justify-content: left;
  align-items: center;
  flex-direction: row;
  margin-left: 6px;
  cursor: pointer;
  font-size: 10px;
  color: var(--dGrey6-color);
}

.annotationTool__color {
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.annotationTool__color--transparent {
  border: 1px solid white;
  opacity: 0.5;
}

.annotationTool__color-arrow {
  margin-left: 6px;
}

.annotationTool__colors-picker {
  position: absolute;
  background: var(--line-tool-bg);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 0 8px 8px;
  z-index: 30;
  top: 47px;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}
</style>
